import {
  DeleteOutlined,
  CameraOutlined,
  DownloadOutlined,
  SaveOutlined,
  PictureOutlined,
  UndoOutlined,
  RedoOutlined,
  BoxPlotOutlined, // ✅ cross icon (trim)
} from "@ant-design/icons";
import { useEffect, useState } from "react";
import PhotoDrawer from "./PhotoDrawer";
import { getLidar } from "../helpers/API";
import { deleteAllSegments } from "../ThreeJs/Segments/Delete";
import { BsMagic } from "react-icons/bs";
import { PiRulerBold, PiSunBold } from "react-icons/pi";
import { LuMountain } from "react-icons/lu";
import { useRecoilState, useResetRecoilState } from "recoil";
import {
  magicToggleState,
  polygonsState,
  selectedIndicesState,
} from "../ThreeJs/ReactStates/states";
import {
  DrawingModes,
  POLYGONS,
  TREESPHERES,
  TREES, // ✅ added (drawn trees list)
} from "../ThreeJs/Global/variables";
import {
  selectSegment,
  showSelectedSegmentLabel,
  toggleSegmentLabels,
} from "../ThreeJs/Segments/Functions";
import { FaGlobe } from "react-icons/fa6";

import { sceneManager } from "../ThreeJs/SetUps/SceneManager";
import { initSunSimulation } from "../ThreeJs/Shading/sunPositioning";
import { PiSolarPanelLight } from "react-icons/pi";
import { TbSunElectricity } from "react-icons/tb";
import { PiTreeBold } from "react-icons/pi";
import { addASAtoScene } from "../ThreeJs/Shading/ASADisplay";
import { DEV_MOD, LAT, LNG } from "../helpers/CONSTANT";
import { enableSphereDrawing } from "../ThreeJs/Trees/treeDrawingUtils";
import { generateShadeReport } from "../helpers/ShadeReport";
import {
  performGlobalUndo,
  performGlobalRedo,
} from "../ThreeJs/Global/UndoController";
import { buildLidarMesh } from "../ThreeJs/Lidar/LidarMeshUtils.js";

// ✅ import to trim house model using existing built-in logic
import { removeTrianglesInSphere } from "../ThreeJs/Trees/removeTree";

// 🔄 import to clear undo history on Save
import { clearUndoStack } from "../ThreeJs/Global/UndoManager";
import { enableTreeDrawing } from "../ThreeJs/Trees/enableTreeDrawing.js";

import { Tooltip, Flex, Popconfirm, message, Button, Dropdown, Menu } from "antd";
import * as THREE from "three";
// use the same custom controls as SceneManager
import { OrbitControls } from "../ThreeJs/Utils/OrbitControls";
import { sanitizeSceneForReportExport } from "../ThreeJs/Global/functions.js";

// ✅ precompute cache + jobs (already in your repo from previous step)
import { ensurePrecomputeForAllSegments } from "../helpers/IrradianceJobs";
import { IRR_CACHE, getOrCreateCache } from "../helpers/IrradianceCache";

// ✅ NEW: build payload + interpret hits
import {
  buildRaycastWork,
  buildRaycastResultFromHits,
} from "../helpers/raycastPayloadBuilder";

// Make THREE & OrbitControls available to child windows
if (typeof window !== "undefined") {
  window.THREE = window.THREE || THREE;
  window.OrbitControls = window.OrbitControls || OrbitControls;
}

/* ---------------------- per-segment ASA helper ---------------------- */
function computeASAForSegment(polygonGTI, polygonResult) {
  const displayPoints = [];
  const monthlyTotals = Array.from({ length: 12 }, () => ({
    shaded: 0,
    unshaded: 0,
    count: 0,
  }));

  const points = polygonResult?.pointResults || [];

  points.forEach((res, index) => {
    const rayResults = res?.rayResults || [];
    const rayLookup = new Map();

    rayResults.forEach((r) => {
      const { day, month, hour } = r.date;
      const key = `${month}-${day}-${hour}`;
      rayLookup.set(key, !r.blocked);
    });

    let totalIrradiance = 0;
    let totalIrradianceUnshaded = 0;

    (polygonGTI || []).forEach((gti) => {
      const dateObj = new Date(gti.datetime);
      const day = dateObj.getDate();
      const month = dateObj.getMonth() + 1;
      const hour = dateObj.getHours();
      const key = `${month}-${day}-${hour}`;

      if (rayLookup.has(key)) {
        const multiplier = rayLookup.get(key) ? 1 : 0;

        totalIrradiance += gti.gti * multiplier;
        totalIrradianceUnshaded += gti.gti;

        monthlyTotals[month - 1].shaded += gti.gti * multiplier;
        monthlyTotals[month - 1].unshaded += gti.gti;
        monthlyTotals[month - 1].count += 1;
      }
    });

    const asa =
      totalIrradianceUnshaded > 0
        ? totalIrradiance / totalIrradianceUnshaded
        : 0;

    displayPoints.push({
      pointIndex: index,
      coordinates: res.position, // Vector3
      totalIrradiance,
      totalIrradianceUnshaded,
      asa,
    });
  });

  const avgASA =
    displayPoints.length > 0
      ? displayPoints.reduce((sum, p) => sum + (p.asa || 0), 0) /
        displayPoints.length
      : 0;

  const monthlyASA = monthlyTotals.map((m, i) => {
    const asa = m.unshaded > 0 ? m.shaded / m.unshaded : 0;
    return { month: i + 1, asa };
  });

  const irradiancePoints = displayPoints.map((p) => ({
    pointIndex: p.pointIndex,
    coordinates: {
      x: p.coordinates?.x ?? 0,
      y: p.coordinates?.y ?? 0,
      z: p.coordinates?.z ?? 0,
    },
    totalIrradiance: p.totalIrradiance,
    totalIrradianceUnshaded: p.totalIrradianceUnshaded,
    asa: p.asa,
  }));

  return { avgASA, monthlyASA, irradiancePoints, displayPoints };
}

/* ---------------------- worker pool runner ---------------------- */
/**
 * Runs GPU raycast jobs "fully parallel" using multiple workers.
 * NOTE: This parallelizes the slow server fetches. Payload building stays on main thread (needs Three.js scene).
 */
async function runGpuRaycastJobsInPool({ jobs, endpointUrl, poolSize = 8 }) {
  const results = new Map();
  const errors = new Map();

  if (!jobs.length) return { results, errors };

  const workerCount = Math.max(1, Math.min(poolSize, jobs.length));
  const workers = [];
  const jobQueue = [...jobs];

  let globalJobId = 1;
  const inflight = new Map(); // jobId -> resolve

  const makeWorker = () =>
    new Worker(new URL("../helpers/gpuRaycastWorker.js", import.meta.url));

  for (let i = 0; i < workerCount; i++) {
    const w = makeWorker();

    w.onmessage = (e) => {
      const msg = e.data || {};
      const { jobId } = msg;

      const resolver = inflight.get(jobId);
      if (resolver) {
        inflight.delete(jobId);
        resolver(msg);
      }
    };

    w.onerror = (err) => {
      console.error("gpuRaycastWorker error:", err);
    };

    workers.push(w);
  }

  const runWorkerLoop = async (worker) => {
    while (jobQueue.length > 0) {
      const job = jobQueue.shift();
      if (!job) break;

      const jobId = globalJobId++;
      const payload = {
        jobId,
        segmentId: job.segmentId,
        endpointUrl,
        payload: job.payload,
      };

      const msg = await new Promise((resolve) => {
        inflight.set(jobId, resolve);
        worker.postMessage(payload);
      });

      if (msg?.ok) {
        results.set(msg.segmentId, msg.hits);
      } else {
        errors.set(msg.segmentId, msg?.error || "Unknown GPU raycast error");
      }
    }
  };

  await Promise.all(workers.map((w) => runWorkerLoop(w)));
  workers.forEach((w) => w.terminate());

  return { results, errors };
}

const ToolHeader = ({
  theme,
  handleUpdatePolygon,
  handleGenerateDxf,
  updateDrawingMode,
  currentDrawingMode,
  arePanelsOK,
  setCurrentSegment,
  currentSegment,
  currentDesign,
  setShadingValues,
  shadingValues,
  setRemovedSections,
}) => {
  const [isPhotoDrawerOpen, setPhotoDrawerOpen] = useState(false);

  const [selectedIndices, setSelectedIndices] =
    useRecoilState(selectedIndicesState);

  const resetPolygonsState = useResetRecoilState(polygonsState);

  const [magicToggle, setMagicToggle] = useRecoilState(magicToggleState);

  const [saveDisabled, setSaveDisabled] = useState(!arePanelsOK);

  const [asaSprites, setAsaSprites] = useState([]);
  const [irradianceLoading, setIrradianceLoading] = useState(false);

  // ✅ tree shape selector (cone/circle)
  const [treeShape, setTreeShape] = useState("cone");
  const [trees, setTrees] = useState([]); // keeps UI in sync (enableTreeDrawing pushes here too)

  let latitude;
  let longitude;

  if (DEV_MOD) {
    latitude = parseFloat(LAT);
    longitude = parseFloat(LNG);
  } else {
    latitude = parseFloat(currentDesign.latitude);
    longitude = parseFloat(currentDesign.longitude);
  }

  const handleUndoClick = () => {
    performGlobalUndo();
  };

  const handleRedoClick = () => {
    performGlobalRedo();
  };

  const handleSaveConfirm = () => {
    handleUpdatePolygon();
    clearUndoStack();
  };

  const togglePhotoDrawer = () => {
    setPhotoDrawerOpen(!isPhotoDrawerOpen);
  };

  const handleMagicToggle = () => {
    setMagicToggle((prev) => !prev);
  };

  useEffect(() => {
    if (magicToggle) {
      selectSegment(
        selectedIndices,
        selectedIndices.segmentIndex,
        setSelectedIndices,
        null
      );
      toggleSegmentLabels(true);
    } else {
      toggleSegmentLabels(false);
      if (selectedIndices.segmentIndex != null) {
        showSelectedSegmentLabel(selectedIndices.segmentIndex);
      }
    }
  }, [magicToggle]);

  useEffect(() => {
    if (selectedIndices.segmentIndex !== null && magicToggle) {
      setMagicToggle((prev) => !prev);
    }
  }, [selectedIndices.segmentIndex]);

  useEffect(() => {
    setSaveDisabled(!arePanelsOK);
  }, [arePanelsOK]);

  const closePhotoDrawer = () => {
    setPhotoDrawerOpen(false);
  };

  const handleScreenshot = () => {
    if (window.captureScreenshot) {
      window.captureScreenshot();
    } else {
      console.error("captureScreenshot function is not available");
    }
  };

  const handleDeleteAll = () => {
    deleteAllSegments();
    setSelectedIndices({ segmentIndex: null, obstacleIndex: null });
    resetPolygonsState();
  };

  const handleRulerClick = () => {
    if (currentDrawingMode === DrawingModes.RULER) {
      updateDrawingMode(DrawingModes.NONE);
    } else if (currentDrawingMode === DrawingModes.NONE) {
      updateDrawingMode(DrawingModes.RULER);
    }
  };

  const handleSunClick = () => {
    const { scene, camera, controls } = sceneManager.getSceneObjects();
    if (!scene || !camera || !controls) {
      console.error("Sun visualization: scene/camera/controls not ready");
      return;
    }
    initSunSimulation(scene, camera, controls, latitude, longitude);
  };

  // ✅ Keep precompute background jobs for TOF/GTI
  useEffect(() => {
    if (!POLYGONS || POLYGONS.length === 0) return;
    ensurePrecomputeForAllSegments(latitude, longitude);
  }, [POLYGONS.length, latitude, longitude]);

const handleIrradiance = async () => {
  if (POLYGONS.length === 0) {
    alert("Please draw segments to analyze shading first.");
    return;
  }

  ensurePrecomputeForAllSegments(latitude, longitude);

  // If everything already computed, toggle visibility
  const allHaveASA = POLYGONS.every((p) => {
    const c = getOrCreateCache(p?.id);
    return (
      c?.status?.asa === "done" &&
      Array.isArray(c?.displayPoints) &&
      c.displayPoints.length > 0
    );
  });

  if (allHaveASA) {
    if (asaSprites.length > 0) {
      const currentlyVisible = asaSprites[0].visible;
      asaSprites.forEach((sprite) => (sprite.visible = !currentlyVisible));
      return;
    }

    const allDisplayPts = POLYGONS.flatMap((p) => {
      const c = getOrCreateCache(p?.id);
      return c?.displayPoints || [];
    });

    if (allDisplayPts.length > 0) {
      const newSprites = addASAtoScene(sceneManager.scene, allDisplayPts);
      setAsaSprites(newSprites);
    }
    return;
  }

  setIrradianceLoading(true);
  await new Promise(requestAnimationFrame);

  // ✅ yield helper (keeps UI responsive during heavy loops)
  const yieldToUI = () => new Promise(requestAnimationFrame);

  // clear old sprites
  if (asaSprites.length > 0) {
    asaSprites.forEach((sprite) => {
      sceneManager.scene.remove(sprite);
      sprite.material.dispose();
      sprite.geometry?.dispose?.();
    });
    setAsaSprites([]);
  }

  // ✅ Load sun positions
  let sunPositions;
  if (typeof window !== "undefined" && typeof window.calculateSunPositions === "function") {
    sunPositions = window.calculateSunPositions(latitude, longitude);
  } else {
    const mod = await import("../ThreeJs/Shading/sunPositioning");
    sunPositions = mod.calculateSunPositions(latitude, longitude);
  }

  sunPositions = (sunPositions || [])
    .filter((s) => s?.position?.z > 0)
    .filter(
      (s, i, arr) =>
        i ===
        arr.findIndex(
          (t) =>
            t.position.x === s.position.x &&
            t.position.y === s.position.y &&
            t.position.z === s.position.z
        )
    );

  // segments that are ready (TOF+GTI done) and missing ASA
  const segmentsToCompute = POLYGONS.filter((seg) => {
    if (!seg?.id) return false;
    const c = getOrCreateCache(seg.id);

    const ready =
      c?.status?.tof === "done" &&
      c?.status?.gti === "done" &&
      Array.isArray(c?.gti) &&
      c.gti.length > 0;

    const needsASA = c?.status?.asa !== "done";
    return ready && needsASA;
  });

  if (segmentsToCompute.length === 0) {
    setIrradianceLoading(false);
    message.info("TOF/GTI are still loading for new segments. Try again in a moment.");
    return;
  }

  // ---------------------------------------------------
  // ✅ NEW: Register Scene Once (triangles only)
  // ---------------------------------------------------

  // helper: gzip JSON (same style as worker)
  async function gzipJsonPayload(payload) {
    if (typeof CompressionStream === "undefined") return null;

    const encoder = new TextEncoder();
    const inputBytes = encoder.encode(JSON.stringify(payload));

    const cs = new CompressionStream("gzip");
    const writer = cs.writable.getWriter();
    writer.write(inputBytes);
    writer.close();

    return await new Response(cs.readable).arrayBuffer();
  }

  const GPU_RAYCAST_URL = "https://lider.dronifyit.com/gpu-raycast/";
  const REGISTER_URL = `${GPU_RAYCAST_URL}register-scene`;

  // ✅ Build one stable "excludeRoots":
  // Exclude ALL segment meshes so triangles are identical across raycasts.
  const excludeRoots = [];

  // all segments (if seg.shape is a mesh/group)
  for (const seg of POLYGONS) {
    if (seg?.shape) excludeRoots.push(seg.shape);
  }

  // also exclude visible lidar meshes (same rule you use in raycast)
  const lidarTerrain = sceneManager.scene.getObjectByName("lidarTerrainMesh");
  if (lidarTerrain?.visible) excludeRoots.push(lidarTerrain);

  const lidarArrow = sceneManager.scene.getObjectByName("lidarArrowControl");
  if (lidarArrow?.visible) excludeRoots.push(lidarArrow);

  // build triangles payload once
  const { buildSceneTrianglesPayload, buildRaycastWork, buildRaycastResultFromHits } =
    await import("../helpers/raycastPayloadBuilder");

  const scenePayload = buildSceneTrianglesPayload(sceneManager.scene, { excludeRoots });

  if (!scenePayload || !scenePayload.triCount) {
    setIrradianceLoading(false);
    message.error("Failed to extract scene triangles for registration.");
    return;
  }

  // ✅ register-scene request (gzip if possible)
  let sceneId = null;
  try {
    const gzBody = await gzipJsonPayload(scenePayload);
    const headers = { "Content-Type": "application/json" };
    const body = gzBody ? gzBody : JSON.stringify(scenePayload);
    if (gzBody) headers["Content-Encoding"] = "gzip";

    const res = await fetch(REGISTER_URL, {
      method: "POST",
      headers,
      body,
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      throw new Error(`${res.status} ${txt}`);
    }

    const json = await res.json();
    sceneId = json?.scene_id;

    if (!sceneId) throw new Error("register-scene missing scene_id");
  } catch (err) {
    console.error("register-scene failed:", err);
    setIrradianceLoading(false);
    message.error("Scene registration failed. Check console.");
    return;
  }

  // ---------------------------------------------------
  // ✅ Build per-segment RAYS-only jobs (small payload)
  // ---------------------------------------------------
  const works = new Map(); // segmentId -> work
  const jobs = [];

  for (let i = 0; i < segmentsToCompute.length; i++) {
    const seg = segmentsToCompute[i];

    const work = buildRaycastWork(
      sceneManager.scene,
      sunPositions,
      seg.shape,
      seg,
      { sceneId } // ✅ NEW
    );

    if (!work?.payload) continue;

    works.set(seg.id, work);

    jobs.push({
      segmentId: seg.id,
      payload: work.payload, // contains scene_id + rays only
    });

    const cache = getOrCreateCache(seg.id);
    cache.status.asa = "pending";
    IRR_CACHE.set(seg.id, cache);

    if (i % 2 === 1) await yieldToUI();
  }

  if (jobs.length === 0) {
    setIrradianceLoading(false);
    message.error("Failed to build ray payloads.");
    return;
  }

  // ✅ parallel raycasts (these are now MUCH smaller)
  const { results: hitsMap, errors } = await runGpuRaycastJobsInPool({
    jobs,
    endpointUrl: GPU_RAYCAST_URL, // still POST /gpu-raycast/
    poolSize: 4,                  // now OK because payload is much smaller
  });

  if (errors.size > 0) {
    errors.forEach((err, segmentId) => {
      const cache = getOrCreateCache(segmentId);
      cache.status.asa = "error";
      cache.error = err;
      IRR_CACHE.set(segmentId, cache);
    });

    message.warning(`${errors.size} segment(s) failed GPU raycast. Check console for details.`);
    console.error("GPU raycast errors:", Object.fromEntries(errors.entries()));
  }

  // ✅ Interpret hits -> compute ASA -> update cache/segment
  for (let i = 0; i < segmentsToCompute.length; i++) {
    const seg = segmentsToCompute[i];
    const segmentId = seg.id;
    const hits = hitsMap.get(segmentId);
    if (!hits) continue;

    const work = works.get(segmentId);
    const rayRes = buildRaycastResultFromHits(work, hits);

    const cache = getOrCreateCache(segmentId);
    const computed = computeASAForSegment(cache.gti, rayRes);

    cache.asa = computed.avgASA;
    cache.monthlyASA = computed.monthlyASA;
    cache.irradiancePoints = computed.irradiancePoints;
    cache.displayPoints = computed.displayPoints;
    cache.status.asa = "done";
    cache.error = null;

    IRR_CACHE.set(segmentId, cache);

    seg.irradiance = {
      ...(seg.irradiance || {}),
      ASA: cache.asa,
      TOF: cache.tof ?? seg.irradiance?.TOF ?? null,
      monthlyASA: cache.monthlyASA,
      irradiancePoints: cache.irradiancePoints,
    };

    await yieldToUI();
  }

  // ✅ Render sprites once after all segments are completed
  const allDisplayPts = POLYGONS.flatMap((p) => {
    const c = getOrCreateCache(p?.id);
    return c?.displayPoints || [];
  });

  if (allDisplayPts.length > 0) {
    const newSprites = addASAtoScene(sceneManager.scene, allDisplayPts);
    setAsaSprites(newSprites);
  }

  setIrradianceLoading(false);
};


  const handleElectricity = () => {
    if (currentSegment === 2) {
      setCurrentSegment(0);
    } else {
      setCurrentSegment(2);
    }
  };

  const LoadingSpinner = () => <div className="spinner"></div>;

  // ✅ Sphere placement mode (used for trimming)
  const startDrawing = () => {
    const houseMeshes = sceneManager?.scene?.children.filter(
      (mesh) => mesh.name === "houseModel"
    );
    enableSphereDrawing(
      sceneManager.scene,
      sceneManager.camera,
      sceneManager.renderer,
      houseMeshes,
      setRemovedSections
    );
  };

  // ✅ remove drawn trees (cone/circle) that intersect the trim sphere
  const removeDrawnTreesInsideSphere = (worldSphere) => {
    try {
      if (!TREES?.trees || TREES.trees.length === 0) return 0;

      let removed = 0;
      const box = new THREE.Box3();
      const treeSphere = new THREE.Sphere();

      for (let i = TREES.trees.length - 1; i >= 0; i--) {
        const tree = TREES.trees[i];
        if (!tree) continue;

        // World-space bounds → bounding sphere
        box.setFromObject(tree);
        box.getBoundingSphere(treeSphere);

        const intersects =
          worldSphere.intersectsSphere(treeSphere) ||
          worldSphere.containsPoint(treeSphere.center);

        if (!intersects) continue;

        // detach transform controls if they are attached to this tree
        if (window.transformControls?.object === tree) {
          try {
            window.transformControls.detach();
          } catch (e) {}
        }

        // remove from scene
        tree.parent?.remove(tree);

        // dispose materials/geometries
        tree.traverse((o) => {
          if (o.geometry) o.geometry.dispose?.();
          if (o.material) {
            if (Array.isArray(o.material))
              o.material.forEach((m) => m.dispose?.());
            else o.material.dispose?.();
          }
        });

        // remove from global + local lists
        TREES.trees.splice(i, 1);
        removed++;
      }

      if (removed > 0) setTrees([...TREES.trees]);

      return removed;
    } catch (e) {
      console.error("Failed to remove drawn trees inside sphere:", e);
      return 0;
    }
  };

  // ✅ Trim action from header icon:
  // 1) trims house model using BVH pipeline
  // 2) also removes drawn trees (cone/circle) intersecting the sphere
  const handleTrimTreeFromHeader = () => {
    const houseGroup = sceneManager?.scene?.getObjectByName("houseModel");
    if (!houseGroup) {
      message.error("House model not found in scene.");
      return;
    }

    // ✅ Prefer selected sphere, else last created sphere
    let sphere =
      window.transformControls?.object ||
      (TREESPHERES?.spheres?.length
        ? TREESPHERES.spheres[TREESPHERES.spheres.length - 1]
        : null);

    // ✅ IMPORTANT: If sphere is not actually in the scene anymore, treat it as null
    if (!sphere || !sphere.isMesh || !sphere.parent) {
      message.info("Click on the model to place a trim sphere.");
      startDrawing();
      return;
    }

    const radius = 1 * (sphere.scale?.x || 1);
    const worldSphere = new THREE.Sphere(sphere.position.clone(), radius);

    // 1) Trim house model
    const removedSection = removeTrianglesInSphere(houseGroup, worldSphere);
    if (removedSection) {
      setRemovedSections((prev) => ({
        ...prev,
        trimmedSections: [...(prev.trimmedSections || []), removedSection],
      }));
    }

    // 2) Trim drawn trees
    const removedTreesCount = removeDrawnTreesInsideSphere(worldSphere);

    // ✅ Remove the sphere from the global TREESPHERES list (THIS was the bug)
    if (TREESPHERES?.spheres?.length) {
      const idx = TREESPHERES.spheres.indexOf(sphere);
      if (idx !== -1) TREESPHERES.spheres.splice(idx, 1);
    }

    // cleanup the sphere + controls
    window.removeSphereAndControls?.(sphere);

    if (!removedSection && removedTreesCount === 0) {
      message.warning("Nothing intersected the trim sphere.");
    } else {
      message.success(
        `Trim done: ${removedSection ? "model" : ""}${
          removedSection && removedTreesCount ? " + " : ""
        }${removedTreesCount ? `${removedTreesCount} drawn tree(s)` : ""}`
      );
    }
  };

  // ✅ start drawing with chosen tree shape
  const startTreeDrawing = (shape = treeShape) => {
    const houseMeshes = sceneManager?.scene?.children.filter(
      (mesh) => mesh.name === "houseModel"
    );

    enableTreeDrawing(
      sceneManager.scene,
      sceneManager.camera,
      sceneManager.renderer,
      houseMeshes,
      setTrees,
      shape // "cone" | "circle"
    );
  };

  // ✅ Tree dropdown menu
  const treeMenu = (
    <Menu
      items={[
        {
          key: "cone",
          label: "Cone Tree",
          onClick: () => {
            setTreeShape("cone");
            startTreeDrawing("cone");
          },
        },
        {
          key: "circle",
          label: "Circle Tree",
          onClick: () => {
            setTreeShape("circle");
            startTreeDrawing("circle");
          },
        },
      ]}
    />
  );

  const downloadShadeReport = () => {
    if (POLYGONS.length === 0) {
      alert("Please draw segments to generate shade report.");
      return;
    }

    const { scene, camera, controls } = sceneManager.getSceneObjects();
    if (!scene || !camera) {
      alert("Scene not ready yet.");
      return;
    }

    const page = window.open("", "_blank");
    if (!page) {
      alert(
        "Popup blocked. Please allow popups for this site to view the report."
      );
      return;
    }

    let initialSnapshot = "";
    try {
      const renderer = sceneManager.renderer;
      if (renderer?.domElement) {
        renderer.render(scene, camera);

        const snapNow = renderer.domElement.toDataURL("image/jpeg", 0.9);
        if (snapNow && snapNow.startsWith("data:image/")) {
          initialSnapshot = snapNow;
        }

        requestAnimationFrame(() => {
          try {
            renderer.render(scene, camera);
            const snap = renderer.domElement.toDataURL("image/jpeg", 0.92);
            if (snap && snap.startsWith("data:image/")) {
              page.postMessage(
                { type: "SHADE_REPORT_SET_SNAPSHOT", snapshot: snap },
                "*"
              );
            }
          } catch (e) {}
        });
      }
    } catch (e) {
      console.warn("Initial snapshot failed:", e);
    }

    const cameraPosition = camera?.position
      ? camera.position.toArray()
      : [0, 0, 50];
    const cameraTarget =
      controls && controls.target && controls.target.toArray
        ? controls.target.toArray()
        : [0, 0, 0];

    const reportPayload = {
      snapshot: initialSnapshot,
      camera: {
        position: cameraPosition,
        target: cameraTarget,
        fov: camera.fov || 45,
      },
      meta: {
        polygonsCount: POLYGONS.length,
      },
      __reportWindowRef: page,
    };

    const onPopupReady = (event) => {
      if (event?.data?.type !== "SHADE_REPORT_READY") return;
      try {
        const renderer = sceneManager.renderer;
        if (renderer?.domElement) {
          renderer.render(scene, camera);
          const snap = renderer.domElement.toDataURL("image/jpeg", 0.92);
          if (snap && snap.startsWith("data:image/")) {
            page.postMessage(
              { type: "SHADE_REPORT_SET_SNAPSHOT", snapshot: snap },
              "*"
            );
          }
        }
      } catch (e) {}
      window.removeEventListener("message", onPopupReady);
    };
    window.addEventListener("message", onPopupReady);

    const onRequestScene = (event) => {
      if (event?.data?.type !== "REQUEST_SHADE_REPORT_SCENE") return;

      try {
        const exportScene = sanitizeSceneForReportExport(THREE, scene);
        const sceneJSON = exportScene.toJSON();

        event.source?.postMessage(
          {
            type: "SHADE_REPORT_SCENE_DATA",
            payload: {
              scene: sceneJSON,
              camera: {
                position: cameraPosition,
                target: cameraTarget,
                fov: camera.fov || 45,
              },
            },
          },
          "*"
        );
      } catch (err) {
        console.error("Failed to export scene for shade report:", err);
        event.source?.postMessage(
          {
            type: "SHADE_REPORT_SCENE_ERROR",
            error: String(err?.message || err),
          },
          "*"
        );
      }
    };

    window.addEventListener("message", onRequestScene);

    generateShadeReport(currentDesign, reportPayload)
      .catch((e) => console.error("generateShadeReport failed:", e))
      .finally(() => {
        setTimeout(
          () => window.removeEventListener("message", onRequestScene),
          120000
        );
      });
  };

  const [lidarData, setLidarData] = useState();
  const [lidarTitle, setLidarTitle] = useState("Lidar");

  useEffect(() => {
    const fetchLidarData = async (lat, lng) => {
      try {
        setLidarTitle("Loading Lidar...");
        const lidarData = await getLidar(lat, lng);

        if (lidarData?.data) {
          setLidarData(lidarData.data);
          setLidarTitle("Show Lidar");
        }
      } catch (err) {
        setLidarTitle("Lidar not available");
        console.error("Error fetching lidar data:", err);
      }
    };

    if (DEV_MOD) {
      fetchLidarData(parseFloat(LAT), parseFloat(LNG));
    } else {
      fetchLidarData(
        parseFloat(currentDesign.latitude),
        parseFloat(currentDesign.longitude)
      );
    }
  }, [currentDesign]);

  const handleLidarToggle = async () => {
    let lidarMesh = sceneManager.scene.getObjectByName("lidarTerrainMesh");

    if (!lidarData) {
      setLidarTitle("Lidar not available");
      return;
    }

    if (lidarMesh) {
      lidarMesh.visible = !lidarMesh.visible;

      const arrow = sceneManager.scene.getObjectByName("lidarArrowControl");
      if (arrow) arrow.visible = lidarMesh.visible;

      setLidarTitle(lidarMesh.visible ? "Hide Lidar" : "Show Lidar");
    } else {
      setLidarTitle("Loading Lidar...");
      await new Promise(requestAnimationFrame);
      await buildLidarMesh(lidarData);
      setLidarTitle("Hide Lidar");
    }
  };

  const menu = (
    <Menu
      items={[
        {
          key: "shade",
          label: "Generate Shade Report",
          onClick: downloadShadeReport,
        },
        {
          key: "dxf",
          label: "Generate DXF Report",
          onClick: handleGenerateDxf,
        },
      ]}
    />
  );

  return (
    <Flex
      style={{
        background: theme ? "#141416" : "#ffffff",
        borderBottom: `1px solid ${!theme ? "#e1e1e1" : "#525252"}`,
        zIndex: "999",
        padding: "10px 20px",
      }}
      className="tool_header_container"
      justify="end"
      align="center"
    >
      <Tooltip title="Delete All Segments">
        <Popconfirm
          title="Are you sure you want to delete all segments?"
          onConfirm={handleDeleteAll}
          okText="Yes"
          cancelText="No"
        >
          <DeleteOutlined
            style={{
              fontSize: "20px",
              cursor: "pointer",
              marginLeft: "20px",
              color: "red",
            }}
          />
        </Popconfirm>
      </Tooltip>

      <Tooltip title="Undo (Ctrl+Z)">
        <UndoOutlined
          onClick={handleUndoClick}
          style={{
            fontSize: "20px",
            cursor: "pointer",
            marginLeft: "20px",
            color: "gray",
          }}
        />
      </Tooltip>

      <Tooltip title="Redo (Ctrl+Y)">
        <RedoOutlined
          onClick={handleRedoClick}
          style={{
            fontSize: "20px",
            cursor: "pointer",
            marginLeft: "20px",
            color: "gray",
          }}
        />
      </Tooltip>

      {/* ✅ Tree dropdown (cone/circle) */}
      <Dropdown overlay={treeMenu} placement="bottomRight" trigger={["click"]}>
        <span>
          <Tooltip title={`Draw Tree (${treeShape})`}>
            <PiTreeBold
              style={{
                fontSize: "20px",
                cursor: "pointer",
                marginLeft: "20px",
                color: "gray",
              }}
            />
          </Tooltip>
        </span>
      </Dropdown>

      {/* ✅ Trim Sphere: trims model + drawn trees */}
      <Tooltip title="Trim (Sphere): trims model + drawn trees">
        <FaGlobe
          onClick={handleTrimTreeFromHeader}
          style={{
            fontSize: "20px",
            cursor: "pointer",
            marginLeft: "20px",
            color: "gray",
          }}
        />
      </Tooltip>

      <Tooltip title="Electricity">
        <TbSunElectricity
          onClick={handleElectricity}
          style={{
            fontSize: "20px",
            cursor: "pointer",
            marginLeft: "20px",
            color: "gray",
          }}
        />
      </Tooltip>

      {!irradianceLoading ? (
        <Tooltip title="Irradiance">
          <PiSolarPanelLight
            onClick={handleIrradiance}
            style={{
              fontSize: "20px",
              cursor: "pointer",
              marginLeft: "20px",
              color: "gray",
            }}
          />
        </Tooltip>
      ) : (
        <Tooltip title="Loading">{LoadingSpinner()}</Tooltip>
      )}

      <Tooltip title="Sun Visualization">
        <PiSunBold
          onClick={handleSunClick}
          style={{
            fontSize: "20px",
            cursor: "pointer",
            marginLeft: "20px",
            color: "gray",
          }}
        />
      </Tooltip>

      <Tooltip title={lidarTitle}>
        <LuMountain
          onClick={handleLidarToggle}
          style={{
            disabled: !sceneManager.scene.getObjectByName("lidarTerrainMesh"),
            fontSize: "20px",
            cursor: "pointer",
            marginLeft: "20px",
            color: "gray",
          }}
        />
      </Tooltip>

      <Tooltip title="Photo Drawer">
        <PictureOutlined
          onClick={togglePhotoDrawer}
          style={{
            fontSize: "20px",
            cursor: "pointer",
            marginLeft: "20px",
            color: "gray",
          }}
        />
      </Tooltip>

      <Tooltip title="Toggle Ruler">
        <PiRulerBold
          onClick={handleRulerClick}
          style={{
            fontSize: "20px",
            cursor: "pointer",
            marginLeft: "20px",
            color: "gray",
          }}
        />
      </Tooltip>

      <Tooltip title="Show All Measurements">
        <BsMagic
          onClick={handleMagicToggle}
          style={{
            fontSize: "20px",
            cursor: "pointer",
            marginLeft: "20px",
            color: "gray",
          }}
        />
      </Tooltip>

      <Tooltip title="Screenshot">
        <CameraOutlined
          onClick={handleScreenshot}
          style={{
            fontSize: "20px",
            cursor: "pointer",
            marginLeft: "20px",
            color: "gray",
          }}
        />
      </Tooltip>

      <Dropdown overlay={menu} placement="bottomRight" trigger={["click"]}>
        <Tooltip title="Generate Report">
          <DownloadOutlined
            style={{
              fontSize: "20px",
              cursor: "pointer",
              marginLeft: "20px",
              color: "gray",
            }}
          />
        </Tooltip>
      </Dropdown>

      <Popconfirm
        title="Are you sure you want to save changes?"
        onConfirm={handleSaveConfirm}
        okText="Yes"
        cancelText="No"
      >
        <Button
          type="primary"
          icon={<SaveOutlined />}
          style={{ marginLeft: "14px" }}
          disabled={saveDisabled}
        >
          Save
        </Button>
      </Popconfirm>

      <PhotoDrawer
        isPhotoDrawerOpen={isPhotoDrawerOpen}
        closePhotoDrawer={closePhotoDrawer}
      />
    </Flex>
  );
};

export default ToolHeader;
